import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/providers/auth-provider';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Check for error in query params first
      const errorParam = searchParams.get('error');
      if (errorParam) {
        setError(getErrorMessage(errorParam));
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Get access_token from query params (refresh_token is set as HttpOnly cookie by backend)
      let accessToken = searchParams.get('access_token');

      if (!accessToken) {
        // Try hash fragment as fallback
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        accessToken = params.get('access_token');
      }

      if (!accessToken) {
        setError('No authentication data received. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        // Use the login function from auth provider with access_token only
        // refresh_token is handled via HttpOnly cookie
        await login({
          access_token: accessToken,
        });

        // Clear URL for security
        window.history.replaceState(null, '', window.location.pathname);

        // Redirect to dashboard
        navigate('/dashboard');
      } catch (error) {
        console.error('Failed to complete OAuth login:', error);
        setError('Failed to complete sign in. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [navigate, login, searchParams]);

  const getErrorMessage = (error: string): string => {
    switch (error) {
      case 'invalid_state':
        return 'Security validation failed. Please try signing in again.';
      case 'provider_mismatch':
        return 'Authentication provider mismatch. Please try again.';
      case 'oauth_failed':
        return 'Authentication failed. Please check your account and try again.';
      case 'access_denied':
        return 'Access was denied. You need to authorize the application.';
      default:
        return 'An error occurred during sign in. Please try again.';
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <p className="text-center text-sm text-muted-foreground">
            Redirecting to login page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <div>
          <h2 className="text-lg font-semibold">Completing sign in...</h2>
          <p className="text-sm text-muted-foreground">Please wait while we log you in</p>
        </div>
      </div>
    </div>
  );
}
